import React, { useState, useRef, useEffect } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import AddIcon from "@atlaskit/icon/core/add";
import MinusIcon from "@atlaskit/icon/core/minus";
import StrokeWeightExtraLargeIcon from "@atlaskit/icon/core/stroke-weight-extra-large";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Expand } from "@/libraries/design/components/expand/src/expand";
import { wacToken } from "@/libraries/design/foundations";
import { useLocalizedHref } from "@/libraries/web-framework";

import { getAgileHubSidebarNavData } from "./agile-hub-sidebar-nav-data";

const desktopCss = css({
    [media.below.md]: {
        display: "none"
    }
});

const tabletMobileCss = css({
    [media.above.md]: {
        display: "none"
    },
    position: "fixed",
    zIndex: 1,
    width: "100%"
});

const navContainerCss = css({
    border: `1px solid ${wacToken("color.neutral.light400")}`,
    borderRadius: wacToken("corner.radius.card"),
    padding: token("space.250"),

    [media.below.md]: {
        backgroundColor: wacToken("color.neutral.light200"),
        padding: "0px",
        border: "none",
        borderRadius: 0
    }
});

const listCss = css({
    listStyle: "none",
    padding: token("space.0"),
    margin: token("space.0")
});

const navHeaderCss = css({
    display: "block",
    padding: `${token("space.200")} 28px`
});

const mobileExpandableButtonCss = css({
    "width": "100%",
    "backgroundColor": wacToken("color.neutral.light200"),
    "border": "none",
    "padding": `${token("space.200")} 60px`,
    "display": "flex",
    "alignItems": "center",
    "gap": token("space.100"),
    "cursor": "pointer",
    "transition": "background-color 0.2s ease",
    "&:hover": {
        backgroundColor: wacToken("color.neutral.light300")
    },
    [media.below.sm]: {
        paddingInlineStart: "32px",
        paddingInlineEnd: "32px"
    }
});

const mobileNavContentCss = css({
    backgroundColor: wacToken("color.neutral.light0"),
    width: "100%",
    paddingInlineStart: "60px",
    paddingInlineEnd: "60px",
    transition: "max-height 0.3s ease, opacity 0.3s ease",
    overflow: "auto",
    [media.below.sm]: {
        paddingInlineStart: "32px",
        paddingInlineEnd: "32px"
    }
});

const mobileNavContentCollapsedCss = css({
    maxHeight: 0,
    opacity: 0
});

const firstLevelNavItemCss = css({
    paddingBlock: token("space.200")
});

const dividerCss = css({
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light400")}`
});

const topicNameCss = css({
    display: "block",
    textAlign: "left"
});

const topicLinkCss = css({
    "transition": "color 0.2s ease",
    "&:hover": {
        color: wacToken("color.primary.blue")
    }
});

const topicNoNestedCss = css({
    paddingInlineStart: "28px"
});

const secondLevelLinkCss = css({
    "display": "flex",
    "gap": token("space.100"),
    "paddingBlock": token("space.100"),
    "transition": "color 0.2s ease",
    "&:hover": {
        color: wacToken("color.primary.blue")
    }
});

const dotContainerCss = css({
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const getCurrRoute = () => {
    let currRoute = window.location.pathname;
    if (currRoute === "/uniform-preview") {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        currRoute = urlParams.get("path") ?? "";
    }
    return currRoute;
};

const NavList = () => {
    const navData = getAgileHubSidebarNavData();
    const localizedHref = useLocalizedHref;
    const getExpandedTopic = (route: string): number => {
        return navData.findIndex(
            (topic) =>
                "nestedLinks" in topic &&
                topic.nestedLinks.some((link) => link.url === route)
        );
    };

    const route = getCurrRoute();
    const expandedTopicIndex = getExpandedTopic(route);
    const lastIndex = navData.length - 1;

    const intl = useIntl();

    return (
        <ul css={listCss}>
            {navData.map((topic, index) => (
                <li
                    key={intl.formatMessage(topic.name)}
                    css={[
                        firstLevelNavItemCss,
                        index !== lastIndex && dividerCss
                    ]}
                >
                    {"url" in topic ? (
                        <a
                            href={localizedHref(topic.url)}
                            aria-current={
                                route === topic.url ? "page" : undefined
                            }
                            css={topicLinkCss}
                        >
                            <span
                                className="h6"
                                css={[topicNameCss, topicNoNestedCss]}
                            >
                                {intl.formatMessage(topic.name)}
                            </span>
                        </a>
                    ) : (
                        <Expand
                            label={
                                <span className="h6">
                                    {intl.formatMessage(topic.name)}
                                </span>
                            }
                            isExpanded={index === expandedTopicIndex}
                        >
                            <ul id={`section-${index}`} css={listCss}>
                                {topic.nestedLinks.map((link) => (
                                    <li key={intl.formatMessage(link.name)}>
                                        <a
                                            href={localizedHref(link.url)}
                                            aria-current={
                                                route === link.url
                                                    ? "page"
                                                    : undefined
                                            }
                                            css={secondLevelLinkCss}
                                        >
                                            <div css={dotContainerCss}>
                                                {route === link.url && (
                                                    <StrokeWeightExtraLargeIcon label="" />
                                                )}
                                            </div>
                                            <span
                                                className="p"
                                                css={
                                                    route === link.url &&
                                                    css({
                                                        fontWeight: "700"
                                                    })
                                                }
                                            >
                                                {intl.formatMessage(link.name)}
                                            </span>
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Expand>
                    )}
                </li>
            ))}
        </ul>
    );
};

const MobileExpandableNav = ({ children }: { children: React.ReactNode }) => {
    const intl = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const [buttonBottom, setButtonBottom] = useState(0);
    const navRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        setIsExpanded(!isExpanded);
    };

    // Calculate button position for nav positioning
    useEffect(() => {
        const updateButtonPosition = () => {
            if (buttonRef.current) {
                const rect = buttonRef.current.getBoundingClientRect();
                setButtonBottom(rect.bottom);
            }
        };

        updateButtonPosition();
        window.addEventListener("resize", updateButtonPosition);
        window.addEventListener("scroll", updateButtonPosition);

        return () => {
            window.removeEventListener("resize", updateButtonPosition);
            window.removeEventListener("scroll", updateButtonPosition);
        };
    }, []);

    // Prevent body scroll when nav is open
    useEffect(() => {
        if (isExpanded) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }

        // Cleanup on unmount
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isExpanded]);

    // Scroll to top when nav expands
    useEffect(() => {
        if (isExpanded && contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, [isExpanded]);

    // Handle click outside to close nav
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isExpanded &&
                navRef.current &&
                !navRef.current.contains(event.target as Node)
            ) {
                setIsExpanded(false);
            }
        };

        if (isExpanded) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isExpanded]);

    return (
        <div ref={navRef} css={css({ position: "relative" })}>
            <button
                ref={buttonRef}
                onClick={handleToggle}
                css={mobileExpandableButtonCss}
                aria-expanded={isExpanded}
                aria-controls="mobile-nav-content"
            >
                <span
                    css={css({
                        height: token("space.300"),
                        width: token("space.300"),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "18px",
                        fontWeight: "bold",
                        color: wacToken("color.primary.blue")
                    })}
                >
                    {isExpanded ? <MinusIcon label="" /> : <AddIcon label="" />}
                </span>
                <span className="eyebrow-md">
                    {intl.formatMessage({
                        id: "agile-hub.sidenav.agile-hub-topics",
                        defaultMessage: "Agile hub topics"
                    })}
                </span>
            </button>
            <div
                ref={contentRef}
                id="mobile-nav-content"
                css={[
                    mobileNavContentCss,
                    isExpanded
                        ? css({
                              position: "fixed",
                              insetBlockStart: `${buttonBottom}px`,
                              insetInlineStart: 0,
                              insetInlineEnd: 0,
                              insetBlockEnd: 0,
                              width: "100vw",
                              height: `calc(100vh - ${buttonBottom}px)`,
                              maxHeight: `calc(100vh - ${buttonBottom}px)`,
                              opacity: 1,
                              zIndex: 10,
                              overflow: "hidden",
                              backgroundColor: wacToken("color.neutral.light0"),
                              paddingBlockStart: token("space.250"),
                              paddingBlockEnd: token("space.300"),
                              paddingInlineStart: "60px",
                              paddingInlineEnd: "60px",
                              [media.below.sm]: {
                                  paddingInlineStart: token("space.400"),
                                  paddingInlineEnd: token("space.400")
                              }
                          })
                        : mobileNavContentCollapsedCss
                ]}
            >
                <div
                    css={css({
                        height: "100%",
                        overflow: "auto",
                        paddingBlockEnd: token("space.300")
                    })}
                >
                    {children}
                </div>
            </div>
        </div>
    );
};

const UniformAgileHubSidebarNav = () => {
    const intl = useIntl();
    return (
        <div aria-label="Sidebar Navigation" css={navContainerCss}>
            <div className="tablet-desktop-nav" css={[desktopCss]}>
                <span className="eyebrow-md" css={navHeaderCss}>
                    {intl.formatMessage({
                        id: "agile-hub.sidenav.browse-topics",
                        defaultMessage: "Browse topics"
                    })}
                </span>
                <div
                    css={css({
                        maxHeight: "calc(100vh - 200px)", // Adjust based on your layout needs
                        overflow: "auto"
                    })}
                >
                    <NavList />
                </div>
            </div>
            <div className="mobile-nav" css={tabletMobileCss}>
                <MobileExpandableNav>
                    <NavList />
                </MobileExpandableNav>
            </div>
        </div>
    );
};

export default UniformAgileHubSidebarNav;
